package one.healthium.today.ui.pcos

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import ifaandroid.feature.today_v2.today_ui_pcos.generated.resources.Res
import ifaandroid.feature.today_v2.today_ui_pcos.generated.resources.ic_plus_50
import loco.GENERAL_STEPS
import loco.PCOS_TODAY_LOG_SYMPTOMS_LABEL
import loco.TODAY_ACTIVITY_LABEL
import loco.TODAY_LOG_CONTROLS_SECTION
import loco.TODAY_LOG_FOOD_BUTTON
import loco.TODAY_WATER_INTAKE_LABEL
import loco.TODAY_WEIGHT_LABEL
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.relativeDate
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.healthKit.ui.HealthKitConnectBanner
import one.healthium.healthKit.ui.HealthKitConnectBannerContract
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.rateus.data.ShowAppRateForStart
import one.healthium.today.ui.TodayUiState
import one.healthium.today.ui.TodayViewModel
import one.healthium.today.ui.calories_counter.CaloriesCounterPCOS
import one.healthium.today.ui.models.DailyStepsUiModel
import one.healthium.today.ui.period_tracker.PeriodTracker
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.Themes
import one.healthium.ui.compose.theme.black30
import one.healthium.ui.compose.theme.black50
import one.healthium.ui.compose.theme.blue_4
import one.healthium.ui.compose.theme.green_14
import one.healthium.ui.compose.theme.green_6
import one.healthium.ui.compose.theme.pink_2
import one.healthium.ui.compose.theme.yellow_4
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

interface TodayEventDelegate {
    fun onMealPlanClicked(id: String)
    fun onLogFoodClicked()
    fun onLogWaterClicked()
    fun onLogWeightClicked()
    fun onLogPeriodClicked()
    fun onLogSymptomsClicked()
}

@Composable
fun TodayScreen(
    eventDelegate: TodayEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = true)

    val viewModel = koinViewModel<TodayViewModel>()
    val state by viewModel.state().collectAsState()

    ShowAppRateForStart()

    TodayScreenContent(
        state = state,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
        onAddFoodClicked = eventDelegate::onLogFoodClicked,
        onLogPeriodClicked = eventDelegate::onLogPeriodClicked,
        onLogSymptomsClicked = eventDelegate::onLogSymptomsClicked,
        healthKitContract = viewModel,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
internal fun TodayScreenContent(
    state: TodayUiState,
    onLogWaterClicked: () -> Unit,
    onLogWeightClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
    onAddFoodClicked: () -> Unit,
    onLogPeriodClicked: () -> Unit,
    onLogSymptomsClicked: () -> Unit,
    healthKitContract: HealthKitConnectBannerContract,
) {
    AppScaffold(Modifier.testTag("today_screen")) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            HeaderImageAndCaloriesCounter(
                state = state,
                onAddFoodClicked = onAddFoodClicked,
                onLogPeriodClicked = onLogPeriodClicked,
                onLogHealthLogClicked = onLogSymptomsClicked,
            )
            Column(
                modifier = Modifier.wrapContentWidth(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                if (state.shouldShowHKBanner) {
                    Spacer(Modifier.height(24.dp))
                    HealthKitConnectBanner(contract = healthKitContract)
                    Spacer(Modifier.height(24.dp))
                } else {
                    Spacer(Modifier.height(12.dp))
                }

                Section(stringResource(loco.Res.string.TODAY_LOG_CONTROLS_SECTION))
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp),
                ) {
                    LogWeightControl(state = state, onClick = onLogWeightClicked)
                    Spacer(Modifier.width(7.dp))
                    LogWaterControl(state = state, onClick = onLogWaterClicked)
                }
                Spacer(Modifier.height(12.dp))

                val todayStepsCounter = state.todayStepsCounter
                Row(
                    modifier = Modifier.padding(horizontal = 12.dp),
                ) {
                    MealPlanControl(state = state, onClick = onMealPlanClicked)
                    Spacer(Modifier.width(7.dp))
                    if (todayStepsCounter != null) {
                        StepsCounterBanner(todayStepsCounter)
                    } else {
                        LogSymptomsControl(onClick = onLogSymptomsClicked)
                    }
                }
                Spacer(Modifier.height(12.dp))
                if (todayStepsCounter != null) {
                    Row(
                        modifier = Modifier.padding(horizontal = 12.dp),
                    ) {
                        LogSymptomsControl(onClick = onLogSymptomsClicked)
                        Spacer(Modifier.width(7.dp))
                        Box(Modifier.size(tileSize))
                    }
                }
            }
            Spacer(Modifier.height(100.dp))
        }
    }
}

@Composable
private fun HeaderImageAndCaloriesCounter(
    state: TodayUiState,
    onAddFoodClicked: () -> Unit,
    onLogPeriodClicked: () -> Unit,
    onLogHealthLogClicked: () -> Unit,
) {
    Column(
        modifier = Modifier.background(AppTheme.colors.secondary),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        PeriodTracker(
            modifier = Modifier.fillMaxWidth(),
            enabled = state.showPeriodTracker,
            onLogPeriodClicked = onLogPeriodClicked,
            onLogHealthLogClicked = onLogHealthLogClicked,
        )
        Spacer(Modifier.height(10.dp))
        CaloriesCounterPCOS(model = state.todayCaloriesCounter)
        Spacer(Modifier.height(24.dp))
        AppButton(
            modifier = Modifier.height(43.dp),
            colors = AppButtonDefaults.invertedSecondaryButtonColors,
            onClick = onAddFoodClicked,
        ) {
            Text(stringResource(loco.Res.string.TODAY_LOG_FOOD_BUTTON))
        }
        Spacer(Modifier.height(24.dp))
    }
}

@Composable
private fun Section(text: String) {
    Text(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
        text = text,
        style = AppTheme.textStyles.secondary_17_500,
        color = AppTheme.colors.text400,
    )
}

@Composable
private fun LogWaterControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: () -> Unit,
) {
    BaseLogControl(
        modifier = modifier,
        containerColor = blue_4,
        icon = AppIcons.icBottle,
        onClick = onClick,
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_WATER_INTAKE_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(6.dp))
        Row(horizontalArrangement = Arrangement.Center) {
            Text(
                text = "${state.todayWaterIntake?.currentValue?.toVolumeString(state.isMetric)}" +
                        " / " +
                        "${state.todayWaterIntake?.dailyAmount?.toVolumeString(state.isMetric)}",
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text100,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = " ${state.isMetric.toVolumeUnit().resolve()}",
                style = AppTheme.textStyles.secondary_17_400,
                color = black30,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun LogWeightControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: () -> Unit
) {
    var weightValue: String? = null
    var dateValue: String? = null

    val latestWeightLog = state.latestWeightLog
    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(state.isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve().orEmpty()
    }

    BaseLogControl(
        modifier = modifier,
        containerColor = pink_2,
        icon = AppIcons.icFileChecked,
        onClick = onClick,
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_WEIGHT_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(6.dp))
        FlowRow(horizontalArrangement = Arrangement.Center) {
            if (!weightValue.isNullOrBlank()) {
                Text(
                    text = weightValue,
                    style = AppTheme.textStyles.secondary_17_500,
                    color = AppTheme.colors.text100,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (!dateValue.isNullOrBlank()) {
                Text(
                    text = ", $dateValue",
                    style = AppTheme.textStyles.secondary_17_400,
                    color = black30,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun StepsCounterBanner(steps: DailyStepsUiModel) {
    BaseLogControl(
        modifier = Modifier,
        containerColor = green_6,
        icon = AppIcons.icSteps
    ) {
        Text(
            text = stringResource(loco.Res.string.TODAY_ACTIVITY_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(6.dp))
        FlowRow(horizontalArrangement = Arrangement.Center) {
            Text(
                text = "${steps.steps} / ${steps.goal}",
                style = AppTheme.textStyles.secondary_17_500,
                color = AppTheme.colors.text100,
                overflow = TextOverflow.Ellipsis,
                maxLines = 1,
            )
            Text(
                text = " ${stringResource(loco.Res.string.GENERAL_STEPS)}",
                style = AppTheme.textStyles.secondary_17_400,
                color = black30,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}


@Composable
private fun MealPlanControl(
    modifier: Modifier = Modifier,
    state: TodayUiState,
    onClick: (MealPlanInfoModel) -> Unit,
) {
    val mealPlan = state.mealPlan

    BaseLogControl(
        modifier = modifier,
        containerColor = yellow_4,
        icon = AppIcons.icPizza,
        onClick = {
            if (mealPlan != null) {
                onClick(mealPlan)
            }
        },
    ) {
        Text(
            text = mealPlan?.name?.resolve().orEmpty(),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun LogSymptomsControl(
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    BaseLogControl(
        modifier = modifier,
        containerColor = green_14,
        icon = Res.drawable.ic_plus_50,
        onClick = onClick,
    ) {
        Text(
            text = stringResource(loco.Res.string.PCOS_TODAY_LOG_SYMPTOMS_LABEL),
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.text100,
            overflow = TextOverflow.Ellipsis,
            textAlign = TextAlign.Center,
        )
    }
}

private val tileSize = DpSize(172.dp, 160.dp)

@Composable
private fun BaseLogControl(
    modifier: Modifier = Modifier,
    containerColor: Color,
    icon: DrawableResource,
    onClick: () -> Unit = {},
    content: @Composable ColumnScope.() -> Unit,
) {
    Surface(
        modifier = modifier.size(tileSize),
        color = containerColor,
        shape = AppTheme.shapes.small,
        onClick = onClick,
    ) {
        Column(
            modifier = modifier
                .fillMaxSize()
                .padding(start = 8.dp, end = 8.dp, top = 24.dp, bottom = 8.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                modifier = Modifier.size(50.dp),
                painter = painterResource(icon),
                tint = black50,
                contentDescription = null,
            )
            Spacer(Modifier.height(12.dp))
            content()
        }
    }
}

@Composable
@Preview(
    showBackground = true,
    widthDp = 600,
    heightDp = 1000,
)
private fun TodayScreenWidthPreview() {
    AppTheme(theme = Themes.pcos()) {
        TodayScreenContent(
            state = TodayUiState.preview,
            onLogWaterClicked = {},
            onLogWeightClicked = {},
            onMealPlanClicked = {},
            onAddFoodClicked = {},
            onLogPeriodClicked = {},
            onLogSymptomsClicked = {},
            healthKitContract = object : HealthKitConnectBannerContract {
                override fun onSkipClicked() = Unit
            }
        )
    }
}

@Composable
@Preview(showBackground = true)
private fun TodayScreenPreview() {
    AppTheme(theme = Themes.pcos()) {
        TodayScreenContent(
            state = TodayUiState.preview,
            onLogWaterClicked = {},
            onLogWeightClicked = {},
            onMealPlanClicked = {},
            onAddFoodClicked = {},
            onLogPeriodClicked = {},
            onLogSymptomsClicked = {},
            healthKitContract = object : HealthKitConnectBannerContract {
                override fun onSkipClicked() = Unit
            }
        )
    }
}